/**
 * Composable для проверки разрешений на основе роли пользователя
 * Определяет, что доступно текущему пользователю в CRM
 */
import { computed } from 'vue'
import { useAuth } from './useAuth'
import type { UserRole } from '../types/auth.types'

export const usePermissions = () => {
  const { user } = useAuth()

  const userRoles = computed<UserRole[]>(() => user.value?.roles ?? [])

  const hasRole = (role: UserRole): boolean => {
    const normalizedRoles = userRoles.value.map((r) => r.toLowerCase())
    return normalizedRoles.includes(role.toLowerCase() as UserRole)
  }

  const hasAnyRole = (...roles: UserRole[]): boolean => {
    const normalizedRoles = userRoles.value.map((r) => r.toLowerCase())
    return roles.some((role) => normalizedRoles.includes(role.toLowerCase() as UserRole))
  }

  const isMarketer = computed(() => hasRole('marketer'))
  const isAdmin = computed(() => hasRole('admin'))
  const isHos = computed(() => hasRole('hos'))
  const isManager = computed(() => hasRole('manager'))
  // DEV-699. Сопровождающий менеджер: видит сопровождаемые сделки, но не
  // создаёт/не отменяет/не удаляет их (row-level фильтрацию делает бэкенд).
  const isSupportManager = computed(() => hasRole('support_manager'))
  const isAgent = computed(() => hasRole('agent'))
  // DEV-1021. Менеджер каталога: доступен ТОЛЬКО раздел «Объекты» (управление
  // ЖК и материалами). Все прочие canAccess* гасятся ниже, canAccessProperties
  // остаётся true.
  const isProjectManager = computed(() => hasRole('project_manager'))

  const canAccessDeals = computed(() => !isMarketer.value && !isProjectManager.value)
  // DEV-699. Создание сделок закрыто для marketer (нет доступа к сделкам вовсе)
  // и support_manager (только сопровождение существующих).
  const canCreateDeals = computed(() => canAccessDeals.value && !isSupportManager.value)
  // DEV-197. Аналитика менеджеров (KPI) — только РОП и администратор.
  const canAccessManagerAnalytics = computed(() => isAdmin.value || isHos.value)
  // DEV-352. Маршрутизация обращений — администратор и РОП.
  const canAccessRoutingRules = computed(() => isAdmin.value || isHos.value)
  // DEV-522. Конструктор воронки канбана — только администратор и РОП (pipeline:*).
  const canConfigurePipeline = computed(() => isAdmin.value || isHos.value)
  // DEV-436. Веб-инбокс обращений агентов — админ, РОП и менеджер («свои vs все» решает бэк).
  const canAccessAgentTickets = computed(() => isAdmin.value || isHos.value || isManager.value)
  // DEV-437. Управление агентами + назначение куратора — администратор и РОП.
  const canManageAgents = computed(() => isAdmin.value || isHos.value)
  // DEV-476. CRM-раздел «Агенты» (реестр) — админ, РОП и менеджер («свои vs все» решает бэк).
  const canAccessAgents = computed(() => isAdmin.value || isHos.value || isManager.value)
  // DEV-1003. Управление доступностью ЖК агентам (тумблер в «Объектах») — админ и РОП.
  const canManageAgentAvailability = computed(() => isAdmin.value || isHos.value)
  // DEV-795. Очередь модерации/приёма компаний-партнёров — менеджер, РОП, админ
  // (ТЗ «менеджер/HOS»). Приём (agents:manage) и просмотр (agents:read) на фронте
  // гейтим одним флагом; финальный RBAC («свои vs все», read vs manage) — на бэке.
  const canModerateCompanyVerifications = computed(
    () => isAdmin.value || isHos.value || isManager.value,
  )
  // DEV-1021. Единственный раздел, доступный менеджеру каталога.
  const canAccessProperties = computed(() => !isMarketer.value)
  // DEV-1082/DEV-1080. Конструктор проекта: форма, юниты и блоки. properties:create/update/delete
  // в auth есть только у ADMIN и PROJECT_MANAGER (HOS/MANAGER — properties:read),
  // поэтому прочим роль вкладку не показываем: она вся про запись.
  const canManageProperties = computed(() => isAdmin.value || isProjectManager.value)
  const canAccessDocuments = computed(() => !isMarketer.value && !isProjectManager.value)
  const canAccessReports = computed(() => !isMarketer.value && !isProjectManager.value)
  const canAccessRcAnalytics = computed(() => !isMarketer.value && !isProjectManager.value)
  // DEV-824. Модерация projectid-маппингов ЖК (complex_source_refs) — админ, CEO, РОП.
  const canModerateRcMappings = computed(() => hasAnyRole('admin', 'ceo', 'hos'))
  // DEV-998. Просмотр Telegram-чатов других менеджеров с клиентом (read-only) —
  // администратор, CEO и РОП. Финальный гейт — на бэкенде (target_manager_id).
  const canViewOtherManagerChats = computed(() => hasAnyRole('admin', 'ceo', 'hos'))
  const canAccessCalendar = computed(() => !isMarketer.value && !isProjectManager.value)
  const canAccessSettings = computed(() => !isMarketer.value && !isProjectManager.value)

  const canEditClients = computed(() => !isMarketer.value && !isProjectManager.value)
  const canCreateClients = computed(() => !isMarketer.value && !isProjectManager.value)
  // DEV-699. support_manager клиентов создаёт, но удалять не может.
  const canDeleteClients = computed(
    () => !isMarketer.value && !isSupportManager.value && !isProjectManager.value,
  )

  const isReadOnly = computed(() => isMarketer.value)

  return {
    userRoles,
    hasRole,
    hasAnyRole,

    isMarketer,
    isAdmin,
    isHos,
    isManager,
    isSupportManager,
    isAgent,
    isProjectManager,

    canAccessDeals,
    canCreateDeals,
    canAccessManagerAnalytics,
    canAccessRoutingRules,
    canConfigurePipeline,
    canAccessAgentTickets,
    canManageAgents,
    canAccessAgents,
    canManageAgentAvailability,
    canModerateCompanyVerifications,
    canAccessProperties,
    canManageProperties,
    canAccessDocuments,
    canAccessReports,
    canAccessRcAnalytics,
    canModerateRcMappings,
    canViewOtherManagerChats,
    canAccessCalendar,
    canAccessSettings,

    canEditClients,
    canCreateClients,
    canDeleteClients,

    isReadOnly,
  }
}
